import { ErrorContext } from "../context/ErrorContext";
import { useContext, useMemo } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { createAxiosInstance } from "../api/axios";
import { hasCompletedPersonalInfo } from "../utils/profileValidation";
import { ProfileCompletionModal } from "./JobDrawer";
import { MapPin, DollarSign, Tag, Calendar } from 'lucide-react';

import {
    Box,
    CardContent,
    Typography,
    Stack,
    Container,
    Divider,
    Button,
    Paper,
    Fade,
    useTheme,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { AuthContext } from "../context/AuthContext";
import WorkIcon from '@mui/icons-material/Work';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';


const JobDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const { setError } = useContext(ErrorContext);
    const { user } = useContext(AuthContext);
    const theme = useTheme();
    const [showProfileModal, setShowProfileModal] = useState(false);
    // const { getToken, isSignedIn } = useAuth();
    // eslint-disable-next-line no-unused-vars
    const [showSignUp, setShowSignUp] = useState(false);

    const authAxios = useMemo(() => {
        return createAxiosInstance(null);
    }, []);

    const fetchJob = async () => {
        setError("Loading..")
        try {
            const response = await authAxios.get(`/job/${id}/`)
            setJob(response.data)
            setError("")
        } catch (error) {
            if (error.response && error.response.status === 403) {
                setError("This job posting may require login to view, or it may not be publicly available")
            } else if (error.response && error.response.status === 404) {
                setError("Job posting not found")
            } else {
                setError("Failed to fetch job details")
            }
        }
    }

    const handleApply = async () => {
        if (!user) {
            // Redirect to auth page (will redirect to new auth system)
            navigate('/auth/sign-in');
            return
        }

        // Check if profile is complete
        if (!hasCompletedPersonalInfo(user)) {
            setShowProfileModal(true);
            return;
        }

        window.location.href = `/opportunities/${id}/apply/`
    }

    // const handleMarkInactive = async () => {
    //     try {
    //         await authAxios.patch(`/job/${id}/`, { is_active: false })
    //         window.location.href = '/'
    //     } catch (error) {
    //         setError("Failed to mark as inactive")
    //     }
    // }

    // const handleMarkActive = async () => {
    //     try {
    //         await authAxios.patch(`/job/${id}/`, { is_active: true })
    //         window.location.href = '/';
    //     } catch (error) {
    //         setError("Failed to mark as active")
    //     }
    // }

    const getPaymentFrequencyDisplay = (frequency) => {
        const frequencies = {
            'YEARLY': '/ Year',
            'MONTHLY': '/ Month',
            'WEEKLY': '/ Week',
            'DAILY': '/ Day',
            'HOURLY': '/ Hour'
        };
        return frequencies[frequency] || '';
    };

    useEffect(() => {
        fetchJob()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Handle hash navigation for sign-in/sign-up toggle
    useEffect(() => {
        const handleHashChange = () => {
            if (window.location.hash === '#sign-up') {
                setShowSignUp(true);
            } else if (window.location.hash === '#sign-in') {
                setShowSignUp(false);
            }
        };

        handleHashChange(); // Check on mount
        window.addEventListener('hashchange', handleHashChange);
        
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [])

    if (!job) {
        return <></>
    }

    // For unauthenticated users, show split screen with auth modal
    if (!user) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: "#f9fafc",
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' }
            }}>
                {/* Left side - Job Details (60%) */}
                <Box sx={{
                    width: { xs: '100%', md: '60%' },
                    overflowY: 'auto',
                    py: { xs: 4, md: 6 },
                    px: { xs: 2, md: 4 }
                }}>
                    <Container maxWidth="lg" disableGutters>
                        <Fade in timeout={1000}>
                            <Box>
                                {/* Breadcrumbs */}
                                <Breadcrumbs sx={{ mb: 3 }}>
                                    <Link
                                        href="/opportunities/"
                                        underline="hover"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'black',
                                        }}
                                    >
                                        <ArrowBackIcon sx={{ mr: 0.5, fontSize: 20 }} />
                                        Back to Opportunities
                                    </Link>
                                </Breadcrumbs>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 0,
                                        overflow: 'visible',
                                        backgroundColor: "#f9fafc",
                                        boxShadow: 'none',
                                        transition: 'none',
                                        '& ul, & ol': {
                                            paddingLeft: '20px',
                                            margin: '8px 0',
                                        },
                                        '& ul': {
                                            listStyleType: 'disc',
                                            listStylePosition: 'outside',
                                        },
                                        '& ol': {
                                            listStyleType: 'decimal',
                                            listStylePosition: 'outside',
                                        },
                                        '& li': {
                                            marginBottom: '4px',
                                            lineHeight: 1.6,
                                            display: 'list-item',
                                        },
                                        '& p': {
                                            margin: '8px 0',
                                            lineHeight: 1.6,
                                        },
                                        '& strong': {
                                            fontWeight: 600,
                                        },
                                        '& em': {
                                            fontStyle: 'italic',
                                        },
                                        '& u': {
                                            textDecoration: 'underline',
                                        },
                                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                                            margin: '16px 0 8px 0',
                                            fontWeight: 600,
                                            color: theme.palette.primary.main,
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                        {/* Header Section */}
                                        <Box sx={{ mb: 4 }}>
                                            <Typography
                                                variant="h4"
                                                gutterBottom
                                                sx={{
                                                    fontSize: '32px',
                                                    fontWeight: '700',
                                                    color: '#222',
                                                    marginBottom: '24px',
                                                    borderBottom: '2px solid #eee',
                                                    paddingBottom: '8px'
                                                }}
                                            >
                                                {job.title}
                                            </Typography>

                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={3}
                                                sx={{ mt: 3 }}
                                            >
                                                {/* Location */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <MapPin size={18} color="#000" />
                                                    <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                        {job.location}
                                                    </Typography>
                                                </Box>

                                                {/* Salary */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <DollarSign size={18} color="#000" />
                                                    <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                        {job.salary}
                                                    </Typography>
                                                </Box>

                                                {/* Category */}
                                                {job.category_details && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Tag size={18} color="#000" />
                                                        <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                            {job.category_details.name}
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Application Deadline */}
                                                {job.application_deadline && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Calendar size={18} color="#000" />
                                                        <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                            Deadline: {new Date(job.application_deadline).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </Box>

                                        <Divider sx={{ my: 3 }} />

                                        {/* Job Details Sections */}
                                        <Stack>
                                            <Section title="Job Description">
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ whiteSpace: 'pre-line' }}
                                                    dangerouslySetInnerHTML={{ __html: job.description }}
                                                />
                                            </Section>

                                            <Section title="Key Responsibilities">
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ whiteSpace: 'pre-line' }}
                                                    dangerouslySetInnerHTML={{ __html: job.responsibilities }}
                                                />
                                            </Section>

                                            <Section title="Required Qualifications">
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ whiteSpace: 'pre-line' }}
                                                    dangerouslySetInnerHTML={{ __html: job.qualifications }}
                                                />
                                            </Section>

                                            {job.additional_qualifications && <Section title="Additional qualifications">
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ whiteSpace: 'pre-line' }}
                                                    dangerouslySetInnerHTML={{ __html: job.additional_qualifications }}
                                                />
                                            </Section>}

                                            {job.benefits && job.benefits.trim() && (
                                                <Section title="Additional Benefits">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.benefits }}
                                                    />
                                                </Section>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Paper>
                            </Box>
                        </Fade>
                    </Container>
                </Box>

                {/* Right side - Auth Modal (40%) */}
                <Box sx={{
                    width: { xs: '100%', md: '40%' },
                    py: { xs: 4, md: 6 },
                    px: { xs: 2, md: 3 },
                    backgroundColor: '#f9fafc',
                    borderLeft: { xs: 'none', md: '1px solid #e2e8f0' }
                }}>
                    <Box sx={{ 
                        position: { xs: 'relative', md: 'sticky' },
                        top: { md: '100px' },
                        width: '100%', 
                        maxWidth: '400px',
                        mx: 'auto'
                    }}>
                        <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', fontWeight: 600 }}>
                            Sign in to apply for this job
                        </Typography>
                        
                        {/* Replaced with message and button to redirect to new auth system */}
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                Please sign in to continue with your application.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/auth/sign-in')}
                                fullWidth
                            >
                                Sign In
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    }

    // For authenticated users, show normal full-width layout
    return (
        <Box sx={{
            minHeight: '100vh',
            background: "#f9fafc",
            py: { xs: 4, md: 6 }
        }}>
            <Container maxWidth="lg">
                <Fade in timeout={1000}>
                    <Box>
                        {/* Breadcrumbs */}
                        <Breadcrumbs sx={{ mb: 3 }}>
                            <Link
                                href="/opportunities/"
                                underline="hover"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'black',
                                }}
                            >
                                <ArrowBackIcon sx={{ mr: 0.5, fontSize: 20 }} />
                                Back to Opportunities
                            </Link>
                        </Breadcrumbs>

                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 0,
                                overflow: 'visible',
                                backgroundColor: "#f9fafc",
                                boxShadow: 'none',
                                transition: 'none',
                                // Styles for HTML content
                                '& ul, & ol': {
                                    paddingLeft: '20px',
                                    margin: '8px 0',
                                },
                                '& ul': {
                                    listStyleType: 'disc',
                                    listStylePosition: 'outside',
                                },
                                '& ol': {
                                    listStyleType: 'decimal',
                                    listStylePosition: 'outside',
                                },
                                '& li': {
                                    marginBottom: '4px',
                                    lineHeight: 1.6,
                                    display: 'list-item',
                                },
                                '& p': {
                                    margin: '8px 0',
                                    lineHeight: 1.6,
                                },
                                '& strong': {
                                    fontWeight: 600,
                                },
                                '& em': {
                                    fontStyle: 'italic',
                                },
                                '& u': {
                                    textDecoration: 'underline',
                                },
                                '& h1, & h2, & h3, & h4, & h5, & h6': {
                                    margin: '16px 0 8px 0',
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                }
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                {/* Header Section */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h4"
                                        gutterBottom
                                        sx={{
                                            fontSize: '32px',
                                            fontWeight: '700',
                                            color: '#222',
                                            marginBottom: '24px',
                                            borderBottom: '2px solid #eee',
                                            paddingBottom: '8px'
                                        }}
                                    >
                                        {job.title}
                                    </Typography>

                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={3}
                                        sx={{ mt: 3 }}
                                    >
                                        {/* Location */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MapPin size={18} color="#000" />
                                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                {job.location}
                                            </Typography>
                                        </Box>

                                        {/* Salary */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DollarSign size={18} color="#000" />
                                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                {job.salary}
                                            </Typography>
                                        </Box>

                                        {/* Payment Frequency */}
                                        {job.payment_frequency && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '14px' }}>
                                                    {getPaymentFrequencyDisplay(job.payment_frequency)}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Category */}
                                        {job.category_details && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Tag size={18} color="#000" />
                                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                    {job.category_details.name}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Application Deadline */}
                                        {job.application_deadline && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Calendar size={18} color="#000" />
                                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                                    Deadline: {new Date(job.application_deadline).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Box>

                                <Divider sx={{ my: 3 }} />

                                        {/* Job Details Sections */}
                                        <Stack>
                                            {job.about_company && job.about_company.trim() && (
                                                <Section title="About Company">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.about_company }}
                                                    />
                                                </Section>
                                            )}

                                            {job.description && job.description.trim() && (
                                                <Section title="Job Description">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.description }}
                                                    />
                                                </Section>
                                            )}

                                            {job.responsibilities && job.responsibilities.trim() && (
                                                <Section title="Key Responsibilities">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.responsibilities }}
                                                    />
                                                </Section>
                                            )}

                                            {job.qualifications && job.qualifications.trim() && (
                                                <Section title="Required Qualifications">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.qualifications }}
                                                    />
                                                </Section>
                                            )}

                                            {job.additional_qualifications && job.additional_qualifications.trim() && (
                                                <Section title="Additional qualifications">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.additional_qualifications }}
                                                    />
                                                </Section>
                                            )}

                                            {job.terms_and_conditions && job.terms_and_conditions.trim() && (
                                                <Section title="Terms and Conditions">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.terms_and_conditions }}
                                                    />
                                                </Section>
                                            )}

                                            {job.benefits && job.benefits.trim() && (
                                                <Section title="Additional Benefits">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ whiteSpace: 'pre-line' }}
                                                        dangerouslySetInnerHTML={{ __html: job.benefits }}
                                                    />
                                                </Section>
                                            )}
                                        </Stack>                                {/* Status and Action Buttons */}
                                <Box sx={{ mt: 6 }}>
                                    {job.status && (
                                        <Typography
                                            sx={{
                                                fontStyle: 'italic',
                                                color: theme.palette.primary.main,
                                                mb: 3
                                            }}
                                        >
                                            You have already applied for this position.
                                        </Typography>
                                    )}

                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                    >
                                        {/* {!job.status && !user?.is_recruiter && ( */}
                                        {!job.status && (
                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={<WorkIcon />}
                                                onClick={handleApply}
                                                sx={{
                                                    px: 4,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    fontWeight: 'bold',
                                                    boxShadow: theme.shadows[4],
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: theme.shadows[8],
                                                    },
                                                    transition: 'all 0.3s ease'
                                                }}
                                            >
                                                {user ? 'Apply Now' : 'Login to Apply'}
                                            </Button>
                                        )}

                                        {/* {user?.is_recruiter && (
                                            <>
                                                {job.is_active && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="large"
                                                        onClick={handleMarkInactive}
                                                        sx={{
                                                            px: 4,
                                                            py: 1.5,
                                                            borderRadius: 2,
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Mark as Inactive
                                                    </Button>
                                                )}
                                                {!job.is_active && (
                                                    <Button
                                                        variant="outlined"
                                                        color="success"
                                                        size="large"
                                                        onClick={handleMarkActive}
                                                        sx={{
                                                            px: 4,
                                                            py: 1.5,
                                                            borderRadius: 2,
                                                            fontWeight: 'bold',
                                                        }}
                                                    >
                                                        Mark as Active
                                                    </Button>
                                                )}

                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="large"
                                                    startIcon={<PeopleIcon />}
                                                    onClick={() => window.location.href = `/opportunities/${id}/applications/`}
                                                    sx={{
                                                        px: 4,
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    View Applications
                                                </Button>

                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="large"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => window.location.href = `/opportunities/${id}/edit/`}
                                                    sx={{
                                                        px: 4,
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        fontWeight: 'bold',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Edit Details
                                                </Button>
                                            </>
                                        )} */}
                                    </Stack>
                                </Box>
                            </CardContent>
                        </Paper>
                    </Box>
                </Fade>
            </Container>

            {/* Profile Completion Modal */}
            <ProfileCompletionModal 
                open={showProfileModal} 
                onClose={() => setShowProfileModal(false)}
                redirectUrl={`/opportunities/${id}/apply`}
            />
        </Box>
    );
};

const Section = ({ title, children }) => (
    <div style={styles.section}>
        <h2 style={styles.heading}>{title}</h2>
        {children}
    </div>
);

const styles = {
    wrapper: {
        padding: '40px 20px',
        backgroundColor: '#f9fafa',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
    },
    container: {
        maxWidth: '800px',
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
        padding: '32px',
        boxSizing: 'border-box',
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#222',
        marginBottom: '24px',
        borderBottom: '2px solid #eee',
        paddingBottom: '8px',
    },
    heading: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#007bff',
        marginBottom: '10px',
    },
    section: {
        marginBottom: '28px',
    },
    text: {
        fontSize: '16px',
        color: '#333',
        lineHeight: 1.6,
    },
    list: {
        paddingLeft: '18px',
        margin: 0,
    },
    listItem: {
        fontSize: '16px',
        color: '#444',
        marginBottom: '6px',
    },
    grid: {
        display: 'flex',
        gap: '40px',
        flexWrap: 'wrap',
    },
    loading: {
        textAlign: 'center',
        fontSize: '18px',
        color: '#666',
        marginTop: '60px',
    },
};

export default JobDetails;